import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthContext } from '@/context/AuthContext';
import { Helmet } from 'react-helmet';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Separator } from '@/components/ui/separator';
import { ArrowLeft } from 'lucide-react';

// Schéma de validation pour le formulaire
const loginSchema = z.object({
  email: z.string().email('Adresse email invalide'),
  csrfToken: z.string(),
});

const passwordSchema = z.object({
  email: z.string().email('Adresse email invalide'),
  password: z.string().min(6, 'Le mot de passe doit contenir au moins 6 caractères'),
  csrfToken: z.string(),
});

type LoginFormValues = z.infer<typeof loginSchema>;
type PasswordFormValues = z.infer<typeof passwordSchema>;

const Login = () => {
  const { signIn, signInWithOTP, loading } = useAuthContext();
  const navigate = useNavigate();
  const [usePassword, setUsePassword] = useState(false);
  const [csrfToken, setCsrfToken] = useState('');
  
  // Générer un token CSRF au chargement de la page
  useEffect(() => {
    // Génération d'un token CSRF simple
    const generateToken = () => {
      const randomBytes = new Uint8Array(32);
      window.crypto.getRandomValues(randomBytes);
      return Array.from(randomBytes)
        .map(byte => byte.toString(16).padStart(2, '0'))
        .join('');
    };
    
    setCsrfToken(generateToken());
  }, []);
  
  // Formulaire pour la connexion par email (OTP)
  const emailForm = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: '',
      csrfToken: csrfToken,
    },
    // Suppression de la propriété values qui écrase les valeurs saisies
  });
  
  // Formulaire pour la connexion par mot de passe
  const passwordForm = useForm<PasswordFormValues>({
    resolver: zodResolver(passwordSchema),
    defaultValues: {
      email: '',
      password: '',
      csrfToken: csrfToken,
    },
    // Suppression de la propriété values qui écrase les valeurs saisies
  });

  // Mettre à jour le token CSRF dans les formulaires lorsqu'il change
  useEffect(() => {
    emailForm.setValue('csrfToken', csrfToken);
    passwordForm.setValue('csrfToken', csrfToken);
  }, [csrfToken, emailForm, passwordForm]);

  // Gérer la soumission du formulaire OTP
  const onSubmitOTP = async (data: LoginFormValues) => {
    // Vérifier que le token CSRF est valide
    if (data.csrfToken !== csrfToken) {
      emailForm.setError('email', { 
        type: 'manual', 
        message: 'Session invalide, veuillez rafraîchir la page' 
      });
      return;
    }
    
    const result = await signInWithOTP(data.email);
    if (result.success) {
      // Rediriger vers une page d'attente ou afficher un message
      // Dans un cas réel, l'utilisateur recevrait un email avec un lien
    }
  };

  // Gérer la soumission du formulaire avec mot de passe
  const onSubmitPassword = async (data: PasswordFormValues) => {
    // Vérifier que le token CSRF est valide
    if (data.csrfToken !== csrfToken) {
      passwordForm.setError('email', { 
        type: 'manual', 
        message: 'Session invalide, veuillez rafraîchir la page' 
      });
      return;
    }
    
    const result = await signIn(data.email, data.password);
    if (result.success) {
      navigate('/dashboard'); // Rediriger vers le tableau de bord après connexion
    }
  };

  // Basculer entre les modes de connexion
  const toggleLoginMode = () => {
    setUsePassword(!usePassword);
    // Synchroniser les emails entre les formulaires
    if (usePassword) {
      emailForm.setValue('email', passwordForm.getValues('email'));
    } else {
      passwordForm.setValue('email', emailForm.getValues('email'));
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Helmet>
        <title>Connexion | Mopaossi</title>
        <meta name="description" content="Connectez-vous à votre compte Mopaossi" />
      </Helmet>
      
      <div className="flex flex-1 h-full">
        {/* Panneau gauche (bleu) - Optimisé pour l'image */}
        <div className="hidden md:flex md:w-1/2 bg-blue-600 flex-col justify-between p-8">
          <div>
            <button
              onClick={() => navigate('/')}
              className="text-white flex items-center gap-2 hover:underline"
            >
              <ArrowLeft size={20} />
              <span>Retour</span>
            </button>
          </div>
          
          {/* Section image optimisée */}
          <div className="flex-1 flex items-center justify-center">
            <div className="relative w-full h-full max-h-[1500px]">
              <img 
                src="https://th.bing.com/th/id/OIP.R4TO9_Uac-pH6zQkoyixQAHaE8?cb=iwp2&pid=ImgDet&w=206&h=137&c=7&dpr=1,3"
                alt="Login illustration"
                className="object-cover w-full h-full rounded-lg shadow-lg"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-leo-blue-500/50 to-transparent flex flex-col justify-end p-6">
                <h1 className="text-4xl font-bold mb-4 text-white">Connexion à votre compte.</h1>
                <p className="text-white/90 text-lg">
                  Accédez à votre tableau de bord et gérez vos projets.
                </p>
              </div>
            </div>
          </div>
          
          <div className="text-white/70 text-sm">
            <p>© {new Date().getFullYear()} - Mopaossi</p>
          </div>
        </div>
        
        {/* Panneau droit (formulaire) */}
        <div className="w-full md:w-1/2 p-8 flex flex-col justify-center">
          <div className="md:hidden mb-6">
            <button
              onClick={() => navigate('/')}
              className="text-blue-600 flex items-center gap-2 hover:underline"
            >
              <ArrowLeft size={20} />
              <span>Retour</span>
            </button>
          </div>
          
          <div className="max-w-md mx-auto w-full">
            {!usePassword ? (
              // Formulaire de connexion par email (OTP)
              <>
                <h2 className="text-2xl font-semibold mb-6">
                  Veuillez entrer votre email pour recevoir un code à usage unique.
                </h2>
                
                <Form {...emailForm}>
                  <form onSubmit={emailForm.handleSubmit(onSubmitOTP)} className="space-y-6">
                    {/* Champ CSRF caché */}
                    <input type="hidden" {...emailForm.register('csrfToken')} />
                    
                    <FormField
                      control={emailForm.control}
                      name="email"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Email</FormLabel>
                          <FormControl>
                            <Input 
                              type="email"
                              placeholder="votre@email.com" 
                              {...field}
                              autoComplete="email"
                              required
                              disabled={loading}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <Button 
                      type="submit" 
                      className="w-full bg-leo-blue text-white hover:bg-blue-800"
                      disabled={loading}
                    >
                      {loading ? 'Envoi en cours...' : 'Continuer'}
                    </Button>
                  </form>
                </Form>
              </>
            ) : (
              // Formulaire de connexion par mot de passe
              <>
                <h2 className="text-2xl font-semibold mb-6">
                  Connectez-vous avec votre mot de passe
                </h2>
                
                <Form {...passwordForm}>
                  <form onSubmit={passwordForm.handleSubmit(onSubmitPassword)} className="space-y-6">
                    {/* Champ CSRF caché */}
                    <input type="hidden" {...passwordForm.register('csrfToken')} />
                    
                    <FormField
                      control={passwordForm.control}  
                      name="email"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Email</FormLabel>
                          <FormControl>
                            <Input 
                              type="email"
                              placeholder="votre@email.com" 
                              {...field}
                              autoComplete="email"
                              required
                              disabled={loading}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    
                    <FormField
                      control={passwordForm.control}
                      name="password"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Mot de passe</FormLabel>
                          <FormControl>
                            <Input 
                              type="password" 
                              placeholder="••••••••" 
                              {...field}
                              autoComplete="current-password"
                              required
                              disabled={loading}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    
                    <Button 
                      type="submit" 
                      className="w-full bg-leo-blue text-white hover:bg-blue-800"
                      disabled={loading}
                    >
                      {loading ? 'Connexion...' : 'Se connecter'}
                    </Button>
                  </form>
                </Form>
              </>
            )}
            
            {/* Séparateur et option de connexion alternative */}
            <div className="mt-8">
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <Separator />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-white text-gray-500">Ou</span>
                </div>
              </div>
              
              <button
                onClick={toggleLoginMode}
                className="mt-4 text-center w-full text-blue-600 hover:underline"
              >
                {usePassword ? 'Se connecter avec un code unique' : 'Se connecter avec votre mot de passe'}
              </button>
            </div>
            
            {/* Option de création de compte */}
            <div className="mt-8 text-center">
              <p className="text-gray-600 mb-4">Votre compte est précieux !</p>
              <div className="grid grid-cols-1 gap-4">
                <Button variant="outline">
                  Espace de compte Administrateur
                </Button>
                <Button variant="outline">
                  Espace de compte Modérateur
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;